import graph from "./graph";
import validate from "validate.js";

import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { fetchUser } from "toolkits/redux/auth";
import { isEmptyObject } from "helpers/formatObject";
import { useDispatch, useSelector } from "react-redux";
import { Chip, Stack, Button, Divider, Typography } from "@mui/material";
import {
  MyInput,
  NewDialog,
  NewDialogTitle,
  NewDialogContent,
  NewDialogActions,
} from "components";

import { digitsFaToEn, numberToWords } from "@persian-tools/persian-tools";

export const schema = {
  amount: {
    presence: { allowEmpty: false, message: "مقدار الزامی است" },
    length: {
      maximum: 64,
    },
  },
};

export default function CreditPopup({ diff = 0 }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const { userToken } = useSelector((state) => state.auth);

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      amount: diff > 0 ? String(diff) : "20000000",
    },
    touched: {},
    errors: {},
  });

  const onOpen = () => setOpen(true);
  const onClose = () => {
    setOpen(false);
    dispatch(fetchUser(userToken));
  };

  const [payment, { loading }] = useMutation(graph.payment.query, {
    context: {
      serviceName: graph.payment.serviceName,
      headers: {
        authorization: `Bearer ${userToken}`,
      },
    },
  });

  const onSubmit = async () => {
    try {
      const { data, errors } = await payment({
        variables: {
          amount: formState.values?.amount,
        },
      });
      if (!errors) {
        if (!isEmptyObject(data)) {
          onClose();
          const { messages, online_payment_refId } = data[graph.payment.name];
          messages.map((message) => toast.success(String(message)));
          if (online_payment_refId) {
            window.location.replace(
              "https://dargaah.com/ird/startpay/" + online_payment_refId
            );
          } else {
            toast.error("خطایی پیش آمد");
          }
        }
      }
    } catch (error) {
      dispatch(fetchUser(userToken));
    }
  };

  const onChange = (event) => {
    event.persist();
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === "checkbox"
            ? event.target.checked
            : digitsFaToEn(event.target.value),
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
  };

  const hasError = (field) => {
    if (formState.touched[field]) {
      const lastErr = formState.errors.find((x) => x[field]);
      return lastErr ? lastErr[field] : "";
    }
  };

  useEffect(() => {
    const errors = validate(formState.values, schema, {
      format: "detailed",
    });
    setFormState((formState) => ({
      ...formState,
      isValid: errors ? false : true,
      errors: errors
        ? errors.map((x) => ({ [x.attribute]: x.options.message }))
        : [],
    }));
  }, [formState.values]);

  const setValue = (amount) =>
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        amount: digitsFaToEn(amount),
      },
      touched: {
        ...formState.touched,
        amount: true,
      },
    }));

  return (
    <>
      <Button
        size="small"
        onClick={onOpen}
        disabled={loading}
        variant="contained"
      >
        فعالسازی
      </Button>
      <NewDialog label="payment" open={open} onClose={onClose}>
        <NewDialogTitle title="افزایش اعتبار" onClose={onClose} />
        <NewDialogContent isPopup>
          <Stack p={2} rowGap={2}>
            <Stack
              rowGap={2}
              height={240}
              width="100%"
              borderRadius={2}
              alignItems="center"
              bgcolor="action.hover"
              justifyContent="center"
            >
              <Typography variant="subtitle1" color="error.main">
                اعتبار شما کافی نیست
              </Typography>
              <Typography variant="body1" color="text.secondary">
                لطفا ابتدا اعتبار خود را افزایش دهید.
              </Typography>
            </Stack>
            <MyInput
              type="number"
              name="amount"
              title="مقدار به تومان"
              placeholder="مقدار به تومان"
              onChange={onChange}
              error={hasError("amount")}
              value={formState.values.amount || ""}
              endIcon={
                <Chip
                  color="primary"
                  label="تومان"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              }
              helperText={hasError("amount")}
              sx={{
                fontSize: 18,
                letterSpacing: 2,
                fontWeight: "bold",
                textAlign: "center",
              }}
            />
            <Stack direction="row" columnGap={1}>
              <Button
                size="small"
                sx={{ flex: 1, borderRadius: 1, bgcolor: "action.hover" }}
                onClick={() => setValue("2000000")}
                disabled={formState.values.amount === "2000000"}
              >
                ۲ میلیون تومان
              </Button>
              <Button
                size="small"
                sx={{ flex: 1, borderRadius: 1, bgcolor: "action.hover" }}
                onClick={() => setValue("5000000")}
                disabled={formState.values.amount === "5000000"}
              >
                ۵ میلیون تومان
              </Button>
              <Button
                size="small"
                sx={{ flex: 1, borderRadius: 1, bgcolor: "action.hover" }}
                onClick={() => setValue("10000000")}
                disabled={formState.values.amount === "10000000"}
              >
                ۱۰ میلیون تومان
              </Button>
            </Stack>
            <Divider />
            <Stack direction="row" columnGap={1} alignItems="center">
              <Chip size="small" label="به حروف" sx={{ borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {numberToWords(formState.values?.amount || "")} تومان
              </Typography>
            </Stack>
          </Stack>
        </NewDialogContent>
        <NewDialogActions>
          <Button
            autoFocus
            size="large"
            variant="contained"
            disabled={loading || !formState.isValid}
            onClick={onSubmit}
            sx={{ minWidth: 160 }}
          >
            پرداخت
          </Button>
        </NewDialogActions>
      </NewDialog>
    </>
  );
}
